// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import _ from "lodash";
import Guard from "../common/guard";
import {
    IAsset, AssetType, IProject, IAssetMetadata, AssetState,
    ILabelData, ILabel,IRegion,RegionType, ITFRecordMetadata,IFormRegion,AssetLabelingState
} from "../models/applicationState";
import { AssetProviderFactory, IAssetProvider } from "../providers/storage/assetProviderFactory";
import { StorageProviderFactory, IStorageProvider } from "../providers/storage/storageProviderFactory";
import { constants } from "../common/constants";
import { appInfo } from "../common/appInfo";
import { encodeFileURI } from "../common/utils";
import { strings, interpolate } from "../common/strings";
import { sha256Hash } from "../common/crypto";
import { toast } from "react-toastify";
import allSettled from "promise.allsettled";


import * as shortid from "shortid";
import { FeatureType } from "../providers/export/tensorFlowRecords/tensorFlowBuilder";
import { TFRecordsReader } from "../providers/export/tensorFlowRecords/tensorFlowReader";
import HtmlFileReader from "../common/htmlFileReader";

/**
 * @name - Asset Service
 * @description - Functions for dealing with project assets
 */
export class AssetService {
    /**
     * Create IAsset from filePath
     * @param filePath - filepath of asset
     * @param fileName - name of asset
     */
    public static async createAssetFromFilePath(filePath: string, fileName?: string): Promise<IAsset> {
        Guard.empty(filePath);

        const normalizedPath = filePath.toLowerCase();

        // If the path is not already prefixed with a protocol
        // then assume it comes from the local file system
        if (!normalizedPath.startsWith("http://") &&
            !normalizedPath.startsWith("https://") &&
            !normalizedPath.startsWith("file:")) {
            // First replace \ character with / the do the standard url encoding then encode unsupported characters
            filePath = encodeFileURI(filePath, true);
        }

        const hash = await sha256Hash(filePath);
        // eslint-disable-next-line
        const pathParts = filePath.split(/[\\\/]/);
        fileName = fileName || pathParts[pathParts.length - 1];
        const fileNameParts = fileName.split(".");

        // eslint-disable-next-line
        const extensionParts = fileNameParts[fileNameParts.length - 1].split(/[\?#]/);
        const assetFormat = extensionParts[0];

        const assetType = this.getAssetType(assetFormat);

        return {
            id: hash,
            format: assetFormat,
            state: AssetState.NotVisited,
            type: assetType,
            name: fileName,
            path: filePath,
            labelData: null,
            ocrContent: null,
            size: null,
        };
    }

    /**
     * Get Asset Type from format (file extension)
     * @param format - File extension of asset
     */
    public static getAssetType(format: string): AssetType {
        switch (format.toLowerCase()) {
            case "jpg":
            case "jpeg":
            case "png":
            case "bmp":
                return AssetType.Image;
            case "tif":
            case "tiff":
                return AssetType.TIFF;
            case "pdf":
                return AssetType.PDF;
            default:
                return AssetType.Unknown;
        }
    }

    private assetProviderInstance: IAssetProvider;
    private storageProviderInstance: IStorageProvider;

    constructor(private project?: IProject) {
        Guard.null(project);
    }

    
    /**
     *
     * @param url - URL for Azure Blob
     */
    public getFileName(url: string) {
        const pathParts = url.split("/");
        return pathParts[pathParts.length - 1].split("?")[0];
    }

    public isSupportedAssetType(assetType: AssetType) {
        return assetType === AssetType.Image || assetType === AssetType.TIFF || assetType === AssetType.PDF;
    }

    /***
     * Get Assets as per interface
     */
    public async getAssetsFromList(assetFile?: any, stateSting?: string): Promise<any> {
        // const files: string[] = await this.listFiles(null/*folderPath*/);
        // console.log(files, "*****files******");
        // const result = [];
        // for (const file of loadedModels) {
        const url = assetFile.path;
        const asset = await AssetService.createAssetFromFilePath(url, assetFile.name);
            // console.log(url, asset, "++++++++++++++++++++++++");
        if (this.isSupportedAssetType(asset.type)) {
                // const labelFileName = decodeURIComponent(`${asset.name}${constants.labelFileExtension}`);
                // const ocrFileName = decodeURIComponent(`${asset.name}${constants.ocrFileExtension}`);
                if (assetFile.ocr_name || stateSting === "doneOcr") {
                    asset.state = AssetState.Visited;
                    asset.ocrContent = assetFile.ocr_content;
                }
                if (assetFile.label_name || stateSting === "doneLabel") {
                    asset.labelFilename = assetFile.label_name;
                    asset.state = AssetState.Tagged;
                } else if (assetFile.ocr_name || stateSting === "doneOcr") {
                    asset.state = AssetState.Visited;
                } else {
                    asset.state = AssetState.NotVisited;
                }
                // result.push(asset);
            }
        // }
        return asset;
    }

    /**
     * Get Asset Provider from project's source connction
     */
    protected get assetProvider(): IAssetProvider {
        if (!this.assetProviderInstance) {
            this.assetProviderInstance = AssetProviderFactory.create(
                this.project.sourceConnection.providerType,
                this.project.sourceConnection.providerOptions,
            );
        }

        return this.assetProviderInstance;
    }

    /**
     * Get Storage Provider from project's target connection
     */
    protected get storageProvider(): IStorageProvider {
        if (!this.storageProviderInstance) {
            this.storageProviderInstance = StorageProviderFactory.create(
                this.project.sourceConnection.providerType,
                this.project.sourceConnection.providerOptions,
            );
        }

        return this.storageProviderInstance;
    }

    /**
     * Get assets from provider
     */
    public async getAssets(): Promise<IAsset[]> {
       // debugger;
        const project = this.project;
        const assets = await this.assetProvider.getAssets();
        const returnedAssets = assets.map((asset) => {
            asset.name = decodeURIComponent(asset.name);
            return asset;
        }).filter((asset) => this.isInExactFolderPath(asset.name, project.folderPath));
        return returnedAssets;
    }

    /** 
     * Save metadata for asset
     * @param metadata - Metadata for asset
     */
    public async save(metadata: IAssetMetadata): Promise<IAssetMetadata> {
        Guard.null(metadata);

      //  debugger;

        //new changes start
        const project = this.project;
        let labelFileName ;
        if(project.projType === 'od')
        { 
            //od code - v2

         labelFileName = decodeURIComponent(`${metadata.asset.name}${constants.assetMetadataFileExtension}`);
        if (metadata) {
            await this.storageProvider.writeText(labelFileName, JSON.stringify(metadata, null, 4));
        }
    }
    else
    {   // OCR Code - v1
        labelFileName = decodeURIComponent(`${metadata.asset.name}${constants.labelFileExtension}`);
        if (metadata.labelData) {
            await this.storageProvider.writeText(labelFileName, JSON.stringify(metadata.labelData, null, 4));
        }

    }
         //new changes end

        /*
        const labelFileName = decodeURIComponent(`${metadata.asset.name}${constants.labelFileExtension}`);
        if (metadata.labelData) {
            await this.storageProvider.writeText(labelFileName, JSON.stringify(metadata.labelData, null, 4));
        }
*/
        if (metadata.asset.state !== AssetState.Tagged) {
            // If the asset is no longer tagged, then it doesn't contain any regions
            // and the file is not required.
            try {
                await this.storageProvider.deleteFile(labelFileName, true, true);
            } catch (err) {
                // The file may not exist - that's OK.
            }
        }
        labelFileName="";
        return metadata;
    }
    /**
     * Get metadata for asset
     * @param asset - Asset for which to retrieve metadata
     */
    public async getAssetMetadata(asset: IAsset): Promise<IAssetMetadata> {
        Guard.null(asset);

          //
          const project= this.project;
      
          if(project.projType === "od")
          {
          const fileName =`${asset.name}${constants.assetMetadataFileExtension}`;
          try {
          const json =await this.storageProvider.readText(fileName);
          return JSON.parse(json) as IAssetMetadata;
  
             } catch (err) {
          if (asset.type === AssetType.TFRecord) {
          return {
                              asset: { ...asset },
                              regions:  await this.getRegionsFromTFRecord(asset),
                              version: appInfo.version,
                          };
                      } else {
          return {
                              asset: { ...asset },
                              regions: [],
                              version: appInfo.version,
                          };
                      }
                  }
          
              }
              const labelData:ILabelData = {
                document: "",
     
    labels: null
              };
          //
       // const labelFileName = decodeURIComponent(`${asset.name}${constants.labelFileExtension}`);
        try {
            /*
            const json = await this.storageProvider.readText(labelFileName, true);
            const labelData = JSON.parse(json) as ILabelData;
            if (!labelData.document || !labelData.labels) {
                const reason = interpolate(strings.errors.missingRequiredFieldInLabelFile.message, { labelFileName });
                toast.error(reason, { autoClose: false });
                throw new Error("Invalid label file");
            }
            if (labelData.labels.length === 0) {
                const reason = interpolate(strings.errors.noLabelInLabelFile.message, { labelFileName });
                toast.info(reason);
                throw new Error("Empty label file");
            }
            if (labelData.labels.find((f) => f.label.trim().length === 0)) {
                toast.error(strings.tags.warnings.emptyName, { autoClose: false });
                throw new Error("Invalid label file");
            }
            if (labelData.labels.containsDuplicates<ILabel>((f) => f.label)) {
                const reason = interpolate(strings.errors.duplicateFieldKeyInLabelsFile.message, { labelFileName });
                toast.error(reason, { autoClose: false });
                throw new Error("Invalid label file");
            }
            const labelHash = new Set<string>();
            for (const label of labelData.labels) {
                const pageSet = new Set<number>();
                for (const valueObj of label.value) {
                    if (pageSet.size !== 0 && !pageSet.has(valueObj.page)) {
                        const reason = interpolate(
                            strings.errors.sameLabelInDifferentPageError.message, { tagName: label.label });
                        toast.error(reason, { autoClose: false });
                        throw new Error("Invalid label file");
                    }
                    pageSet.add(valueObj.page);
                    for (const box of valueObj.boundingBoxes) {
                        const hash = [valueObj.page, ...box].join();
                        if (labelHash.has(hash)) {
                            const reason = interpolate(
                                strings.errors.duplicateBoxInLabelFile.message, { page: valueObj.page });
                            toast.error(reason, { autoClose: false });
                            throw new Error("Invalid label file");
                        }
                        labelHash.add(hash);
                    }
                }
            }
            
            
            toast.dismiss();
            
            */
            return {
                asset: { ...asset },
                regions: [],
                version: appInfo.version,
                labelData,
            };
        } catch (err) {
            if (err instanceof SyntaxError) {
               // const reason = interpolate(strings.errors.invalidJSONFormat.message, { labelFileName });
               // toast.error(reason, { autoClose: false });
            }
            return {
                asset: { ...asset },
                regions: [],
                version: appInfo.version,
                labelData: null,
            };
    }

}

    /**
     * Delete a tag from asset metadata files
     * @param tagName Name of tag to delete
     */
    public async deleteTag(tagName: string, appStateAssets: any): Promise<IAssetMetadata[]> {
        const transformer = (tagNames) => tagNames.filter((t) => t !== tagName);
        const labelTransformer = (labelData: ILabelData) => {
            labelData.labels = labelData.labels.filter((label) => label.label !== tagName);
            return labelData;
        };
        return await this.getUpdatedAssets(tagName, transformer, labelTransformer, appStateAssets);
    }

    /**
     * Delete a tag from asset metadata files
     * @param tagName Name of tag to delete
     */
    public async deleteFileTag(tagName: string, asset: IAsset): Promise<IAssetMetadata[]> {
        const transformer = (tagNames) => tagNames.filter((t) => t !== tagName);
        const labelTransformer = (labelData: ILabelData) => {
            labelData.labels = labelData.labels.filter((label) => label.label !== tagName);
            return labelData;
        };
        return await this.getUpdatedAsset(tagName, transformer, labelTransformer, asset);
    }

    /**
     * Rename a tag within asset metadata files
     * @param tagName Name of tag to rename
     */
    public async renameTag(tagName: string, newTagName: string, appStateAssets?: any): Promise<IAssetMetadata[]> {
        const transformer = (tags) => tags.map((t) => (t === tagName) ? newTagName : t);
        const labelTransformer = (labelData: ILabelData) => {
            const field = labelData.labels.find((label) => label.label === tagName);
            if (field) {
                field.label = newTagName;
            }
            return labelData;
        };
        return await this.getUpdatedAssets(tagName, transformer, labelTransformer, appStateAssets);
    }

    /**
     * Update tags within asset metadata files
     * @param tagName Name of tag to update within project
     * @param transformer Function that accepts array of tags from a region and returns a modified array of tags
     */
    private async getUpdatedAsset(
        tagName: string,
        transformer: (tags: string[]) => string[],
        labelTransformer: (label: ILabelData) => ILabelData,
        assetFile: IAsset)
        : Promise<IAssetMetadata[]> {
            const updates = await _.values(this.project.assets).mapAsync(async (asset) => {
                let isUpdated;
                const assetMetadata = await this.getAssetMetadata(asset);
                if (assetFile.id === asset.id) {
                isUpdated = this.updateTagInAssetMetadata(assetMetadata, tagName, transformer, labelTransformer);
                }
                return isUpdated ? assetMetadata : null;
            });
            return updates.filter((assetMetadata) => !!assetMetadata);
    }
    /**
     * Update tags within asset metadata files
     * @param tagName Name of tag to update within project
     * @param transformer Function that accepts array of tags from a region and returns a modified array of tags
     */
    private async getUpdatedAssets(
        tagName: string,
        transformer: (tags: string[]) => string[],
        labelTransformer: (label: ILabelData) => ILabelData,
        appStateAsssets?: (appStateAsssets: IAsset[]) => IAsset[])
        : Promise<IAssetMetadata[]> {
        // Loop over assets and update if necessary
        const updates = await _.values(appStateAsssets).mapAsync(async (asset) => {
            let assetMetadata;
            let isUpdated;
            if (asset && asset.state === AssetState.Tagged) {
             assetMetadata = await this.getAssetMetadata(asset);
             isUpdated = this.updateTagInAssetMetadata(assetMetadata, tagName, transformer, labelTransformer);
            }
            return isUpdated ? assetMetadata : null;
        });

        return updates.filter((assetMetadata) => !!assetMetadata);
    }

    /**
     * Update tag within asset metadata object
     * @param assetMetadata Asset metadata to update
     * @param tagName Name of tag being updated
     * @param transformer Function that accepts array of tags from a region and returns a modified array of tags
     * @returns Modified asset metadata object or null if object does not need to be modified
     */
    private updateTagInAssetMetadata(
        assetMetadata: IAssetMetadata,
        tagName: string,
        transformer: (tags: string[]) => string[],
        labelTransformer: (labelData: ILabelData) => ILabelData): boolean {
        let foundTag = false;

        for (const region of assetMetadata.regions) {
            if (region.tags.find((t) => t === tagName)) {
                foundTag = true;
                region.tags = transformer(region.tags);
            }
        }

        if (assetMetadata.labelData && assetMetadata.labelData.labels) {
            const field = assetMetadata.labelData.labels.find((field) => field.label === tagName);
            if (field) {
                foundTag = true;
                assetMetadata.labelData = labelTransformer(assetMetadata.labelData);
            }
        }

        if (foundTag) {
            assetMetadata.regions = assetMetadata.regions.filter((region) => region.tags.length > 0);
            assetMetadata.asset.state = _.get(assetMetadata, "labelData.labels.length")
                ? AssetState.Tagged : AssetState.Visited;
            return true;
        }

        return false;
    }

    private isInExactFolderPath = (assetName: string, normalizedPath: string): boolean => {
        if (normalizedPath === "") {
            return assetName.lastIndexOf("/") === -1;
        }

        const startsWithFolderPath = assetName.indexOf(`${normalizedPath}/`) === 0;
        return startsWithFolderPath && assetName.lastIndexOf("/") === normalizedPath.length;
    }

    //NEW
    private getOcrFromAnalyzeResult(analyzeResult: any) {
        return _.get(analyzeResult, "analyzeResult.readResults", []);
    }
    async uploadAssetPredictResult(asset: IAsset, readResults: any): Promise<void> {
        const getBoundingBox = (pageIndex, arr: number[]) => {
            const ocrForCurrentPage: any = this.getOcrFromAnalyzeResult(readResults)[pageIndex - 1];
            const ocrExtent = [0, 0, ocrForCurrentPage.width, ocrForCurrentPage.height];
            const ocrWidth = ocrExtent[2] - ocrExtent[0];
            const ocrHeight = ocrExtent[3] - ocrExtent[1];
            const result = [];
            for (let i = 0; i < arr.length; i += 2) {
                result.push([
                    (arr[i] / ocrWidth),
                    (arr[i + 1] / ocrHeight),
                ]);
            }
            return result;
        };
        const getLabelValues = (field: any) => {
            return field.elements.map((path: string) => {
                const pathArr = path.split('/').slice(1);
                const word = pathArr.reduce((obj: any, key: string) => obj[key], { ...readResults.analyzeResult });
                return {
                    page: field.page,
                    text: word.text || word.state,
                    confidence: word.confidence,
                    boundingBoxes: [getBoundingBox(field.page, word.boundingBox)]
                };
            });
        };
        const labels = [];
        readResults.analyzeResult.documentResults
            .map(result => Object.keys(result.fields)
                .filter(key => result.fields[key])
                .map<ILabel>(key => (
                    {
                        label: key,
                        key: null,
                        value: getLabelValues(result.fields[key])
                    }))).forEach(items => {
                        labels.push(...items);
                    });

        if (labels.length > 0) {
            const fileName = decodeURIComponent(asset.name).split('/').pop();
            const labelData: ILabelData = {
                document: fileName,
                labels
            };
            const metadata = {
                ...await this.getAssetMetadata(asset),
                labelData
            };
            metadata.asset.state = AssetState.Tagged;

            const ocrData = JSON.parse(JSON.stringify(readResults));
            delete ocrData.analyzeResult.documentResults;
            if (ocrData.analyzeResult.errors) {
                delete ocrData.analyzeResult.errors;
            }
            const ocrFileName = `${asset.name}${constants.ocrFileExtension}`;
            await Promise.all([
                this.save(metadata),
                this.storageProvider.writeText(ocrFileName, JSON.stringify(ocrData, null, 2))
            ]);
        }
        else {
            const ocrData = { ...readResults };
            delete ocrData.analyzeResult.documentResults;
            if (ocrData.analyzeResult.errors) {
                delete ocrData.analyzeResult.errors;
            }
            const labelFileName = decodeURIComponent(`${asset.name}${constants.labelFileExtension}`);
            const ocrFileName = decodeURIComponent(`${asset.name}${constants.ocrFileExtension}`);
            try {
                await Promise.all([
                    this.storageProvider.deleteFile(labelFileName, true, true),
                    this.storageProvider.writeText(ocrFileName, JSON.stringify(ocrData, null, 2))
                ]);
            }
            catch{
                return;
            }
        }
    }

    /**
     * Delete asset
     * @param metadata - Metadata for asset
     */
    public async delete(metadata: IAssetMetadata): Promise<void> {
        Guard.null(metadata);

        const labelFileName = decodeURIComponent(`${metadata.asset.name}${constants.labelFileExtension}`);
        const ocrFileName = decodeURIComponent(`${metadata.asset.name}${constants.ocrFileExtension}`);
        const assetFileName = decodeURIComponent(`${metadata.asset.name}`);
        await allSettled(
            [
                this.storageProvider.deleteFile(labelFileName, true, true),
                this.storageProvider.deleteFile(ocrFileName, true, true),
                this.storageProvider.deleteFile(assetFileName, true, true)
            ]
        );
    }

    
    /**
     * Get a list of child assets associated with the current asset
     * @param rootAsset The parent asset to search
     */
    public getChildAssets(rootAsset: IAsset): IAsset[] {
        Guard.null(rootAsset);

        if (rootAsset.type !== AssetType.Video) {
            return [];
        }

        return _
            .values(this.project.assets)
            .filter((asset) => asset.parent && asset.parent.id === rootAsset.id)
            .sort((a, b) => a.timestamp - b.timestamp);
    }

    

    private async getRegionsFromTFRecord(asset: IAsset): Promise<IRegion[]> {
        const objectArray = await this.getTFRecordMetadata(asset);
        const regions: IRegion[] = [];

        // Add Regions from TFRecord in Regions
        for (let index = 0; index < objectArray.textArray.length; index++) {
            regions.push({
                id: shortid.generate(),
                type: RegionType.Rectangle,
                tags: [objectArray.textArray[index]],
                boundingBox: {
                    left: objectArray.xminArray[index] * objectArray.width,
                    top: objectArray.yminArray[index] * objectArray.height,
                    width: (objectArray.xmaxArray[index] - objectArray.xminArray[index]) * objectArray.width,
                    height: (objectArray.ymaxArray[index] - objectArray.yminArray[index]) * objectArray.height,
                },
                points: [{
                    x: objectArray.xminArray[index] * objectArray.width,
                    y: objectArray.yminArray[index] * objectArray.height,
                },
                {
                    x: objectArray.xmaxArray[index] * objectArray.width,
                    y: objectArray.ymaxArray[index] * objectArray.height,
                }],
             
            });
        }

        return regions;
    }

    
    private async getTFRecordMetadata(asset: IAsset): Promise<ITFRecordMetadata> {
        const tfrecords = new Buffer(await HtmlFileReader.getAssetArray(asset));
        const reader = new TFRecordsReader(tfrecords);

        const width = reader.getFeature(0, "image/width", FeatureType.Int64) as number;
        const height = reader.getFeature(0, "image/height", FeatureType.Int64) as number;

        const xminArray = reader.getArrayFeature(0, "image/object/bbox/xmin", FeatureType.Float) as number[];
        const yminArray = reader.getArrayFeature(0, "image/object/bbox/ymin", FeatureType.Float) as number[];
        const xmaxArray = reader.getArrayFeature(0, "image/object/bbox/xmax", FeatureType.Float) as number[];
        const ymaxArray = reader.getArrayFeature(0, "image/object/bbox/ymax", FeatureType.Float) as number[];
        const textArray = reader.getArrayFeature(0, "image/object/class/text", FeatureType.String) as string[];

        return { width, height, xminArray, yminArray, xmaxArray, ymaxArray, textArray };
    }

    public async uploadBuffer(name: string, buffer: Buffer) {
        const path = this.project.folderPath ? `${this.project.folderPath}/${name}` : name;
        await this.storageProvider.writeBinary(path, buffer);
    }

    async syncAssetPredictResult(asset: IAsset, predictResults: any): Promise<IAssetMetadata> {
        const assetMeatadata = this.getAssetPredictMetadata(asset, predictResults);
        const ocrData = _.cloneDeep(predictResults);
        delete ocrData.analyzeResult.documentResults;
        if (ocrData.analyzeResult.errors) {
            delete ocrData.analyzeResult.errors;
        }
        const ocrFileName = `${asset.name}${constants.ocrFileExtension}`;
        if (assetMeatadata) {


            await Promise.all([
                this.save(assetMeatadata),
                this.storageProvider.writeText(ocrFileName, JSON.stringify(ocrData, null, 2))
            ]);
            return assetMeatadata;
        }
        else {
            const labelFileName = decodeURIComponent(`${asset.name}${constants.labelFileExtension}`);
            try {
                await Promise.all([
                    this.storageProvider.deleteFile(labelFileName, true, true),
                    this.storageProvider.writeText(ocrFileName, JSON.stringify(ocrData, null, 2))
                ]);
            } catch (err) {
                // The label file may not exist - that's OK.
            }
            return null;
        }
    }

    getAssetPredictMetadata(asset: IAsset, predictResults: any): IAssetMetadata {
        asset = _.cloneDeep(asset);
        const getBoundingBox = (pageIndex, arr: number[]) => {
            const ocrForCurrentPage: any = this.getOcrFromAnalyzeResult(predictResults)[pageIndex - 1];
            const ocrExtent = [0, 0, ocrForCurrentPage.width, ocrForCurrentPage.height];
            const ocrWidth = ocrExtent[2] - ocrExtent[0];
            const ocrHeight = ocrExtent[3] - ocrExtent[1];
            const result = [];
            for (let i = 0; i < arr.length; i += 2) {
                result.push(...[
                    (arr[i] / ocrWidth),
                    (arr[i + 1] / ocrHeight),
                ]);
            }
            return result;
        };
        const getLabelValues = (field: any) => {
            return field.elements?.map((path: string): IFormRegion => {
                const pathArr = path.split('/').slice(1);
                const word = pathArr.reduce((obj: any, key: string) => obj[key], {...predictResults.analyzeResult});
                return {
                    page: field.page,
                    text: word.text || word.state,
                    boundingBoxes: [getBoundingBox(field.page, word.boundingBox)]
                };
            });
        };
        const labels =
            predictResults.analyzeResult.documentResults
                .map(result => Object.keys(result.fields)
                    .filter(key => result.fields[key])
                    .map<ILabel>(key => (
                        {
                            label: key,
                            key: null,
                            confidence: result.fields[key].confidence,
                            value: getLabelValues(result.fields[key])
                        }))).flat(2);

        const fileName = decodeURIComponent(asset.name).split('/').pop();
        const labelData: ILabelData = {
            document: fileName,
            labels: []
        };
        const metadata: IAssetMetadata = {
            asset: {...asset},
            regions: [],
            version: appInfo.version,
            labelData,
        }
        if (labels.length > 0) {
            labelData.labelingState = AssetLabelingState.AutoLabeled;
            labelData.labels = labels;
            metadata.asset.labelingState = AssetLabelingState.AutoLabeled;
            metadata.asset.state = AssetState.Tagged;
        }
        return metadata;
    }




}
